<template>
    <vx-expandable-card>
        <template v-slot:header> What </template>
        <template v-slot:content>
            <strong class="text-slate-900 text-sm font-semibold mb-2 block sm:text-base"> Message </strong>
            <input-with-errors :errors="props.errors">
                <template v-slot:alert="{ error }">
                    <vx-alert color="danger" :closeable="false">
                        <template v-slot:icon>
                            <font-awesome-icon :icon="faTriangleExclamation" widthAuto></font-awesome-icon>
                        </template>
                        {{ error }}
                        <template v-slot:right>
                            <vx-badge size="xs" class="ml-1 text-[10px] whitespace-nowrap" color="danger">
                                Set by Corp <font-awesome-icon :icon="faCrown" />
                            </vx-badge>
                        </template>
                    </vx-alert>
                </template>

                <message-builder
                    ref="messageBuilder"
                    v-model="message.body"
                    v-model:mediaUrl="message.media_url"
                    v-model:mediaContentType="message.media_content_type"
                    @selected:snippet="emit('snippetUsed', $event.body)"
                    :error="displayFirstError(validate.body.$errors)"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    preview
                >
                    <template v-if="FEATURES.quick_blast_ai" v-slot:actions>
                        <vx-dropdown-item @click.prevent="aiModal = true">
                            <span class="flex items-center gap-1">
                                <font-awesome-icon
                                    widthAuto
                                    :icon="faWandMagicSparkles"
                                    class="text-sm"
                                ></font-awesome-icon>
                                <span>AI Message</span>
                                <experimental-pill class="pointer-events-none" placement="right"></experimental-pill>
                            </span>
                        </vx-dropdown-item>
                    </template>
                </message-builder>
            </input-with-errors>

            <message-builder-quick-blast-generation
                v-if="FEATURES.quick_blast_ai"
                v-model:visible="aiModal"
                :campaign-type="props.campaignType"
                @selected="message.body = $event"
            ></message-builder-quick-blast-generation>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxDropdownItem, VxAlert, VxBadge } from '@voxie/frontend-components';
import { inject, ref, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation, faCrown } from '@fortawesome/pro-solid-svg-icons';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import MessageBuilderQuickBlastGeneration from '~/components/general/message-builder/modals/MessageBuilderQuickBlastGeneration.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';
import ExperimentalPill from '~/components/general/ExperimentalPill.vue';
import InputWithErrors from '~/components/general/InputWithErrors.vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import { useGroupMessageRules } from '~/components/quick-blast/useGroupMessageRules';

const FEATURES = inject('FEATURES');

const { shouldBlockImages, shouldBlockFiles } = useGroupMessageRules();

const props = defineProps({
    campaignType: {
        type: String,
        required: true,
        validator: (campaignType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(campaignType),
    },
    errors: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['snippetUsed']);

const message = defineModel({ type: Object, required: true, default: () => ({}) });

const aiModal = ref(false);

const blockImages = computed(() => shouldBlockImages(props.campaignType));
const blockFiles = computed(() => shouldBlockFiles(props.campaignType));

const validate = useVuelidate(
    {
        body: {
            required: helpers.withMessage('Message is required', required),
        },
    },
    message
);
</script>
